import { useUiStrings } from "@seagames/ui";
import { useId } from "react";

/**
 * The GMS medal: a ribbon and a shaded disc in real gold / silver / bronze —
 * every medal the GMS draws goes through here (user, 2026-10-30: "medal
 * icon, proper colours, not just a circle"). Sized in `em`, so it follows
 * the text beside it. The colour is never alone: callers name the medal in
 * a label, a title or the column header.
 */
export type MedalKind = "gold" | "silver" | "bronze";

const TONES: Record<MedalKind, { hi: string; mid: string; lo: string; rim: string }> = {
	gold: { hi: "#fff1b0", mid: "#f2c230", lo: "#b8860b", rim: "#9a6f00" },
	silver: { hi: "#ffffff", mid: "#c9ced9", lo: "#8a92a3", rim: "#6f7787" },
	bronze: { hi: "#f8cfab", mid: "#cf8a57", lo: "#95552a", rim: "#7c4520" },
};

export function MedalIcon({ medal, className = "" }: { medal: MedalKind; className?: string }) {
	const id = useId();
	const c = TONES[medal];
	return (
		<svg viewBox="0 0 20 24" aria-hidden focusable="false" className={`inline-block h-[1.25em] w-auto shrink-0 align-[-0.25em] ${className}`}>
			<defs>
				<linearGradient id={`${id}d`} x1="0.2" y1="0" x2="0.8" y2="1">
					<stop offset="0" stopColor={c.hi} />
					<stop offset="0.45" stopColor={c.mid} />
					<stop offset="1" stopColor={c.lo} />
				</linearGradient>
			</defs>
			{/* Ribbon: two strips meeting behind the disc. */}
			<path d="M3 0h5l4.2 9.5-3.6 2.2z" fill="#2b3a8f" />
			<path d="M17 0h-5L7.8 9.5l3.6 2.2z" fill="#d6262c" />
			<circle cx="10" cy="15.5" r="7.6" fill={`url(#${id}d)`} stroke={c.rim} strokeWidth="1" />
			<circle cx="10" cy="15.5" r="5.1" fill="none" stroke={c.rim} strokeOpacity="0.45" strokeWidth="0.8" />
			<path
				d="M10 12.1l1 2.05 2.25.33-1.63 1.59.39 2.24L10 17.25l-2.01 1.06.39-2.24-1.63-1.59 2.25-.33z"
				fill={c.hi}
				stroke={c.rim}
				strokeOpacity="0.55"
				strokeWidth="0.5"
			/>
		</svg>
	);
}

/**
 * A medal with its count beside it ("🏅 7"). `label` names medal and count
 * for screen readers and the tooltip; a zero count is drawn faded.
 */
export function MedalCount({ medal, value, label, className = "" }: { medal: MedalKind; value: number; label?: string; className?: string }) {
	const { formatNumber: fmt } = useUiStrings();
	return (
		<span
			className={`inline-flex items-center gap-[0.1875em] font-bold tabular-nums leading-none ${value === 0 ? "opacity-45" : ""} ${className}`}
			title={label}
		>
			<MedalIcon medal={medal} />
			{label && <span className="sr-only">{label}</span>}
			<span aria-hidden={label ? true : undefined}>{fmt(value)}</span>
		</span>
	);
}
